import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Typography } from '@mui/material_modern'
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material_modern'
import { Autorenew } from '@mui/icons-material'
import { makeStyles, useTheme } from '@mui/styles'
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { DISPATCH_STATUS_ICONS_DICT } from 'components/containers/load-board/utils/constants'
import { generateRenderHeaderFunction } from 'utils/generateRenderHeaderFunction'
import { SORTABLE_DISPATCH_TICKET_HISTORY_FIELDS } from 'utils/constants'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  },
}))

const DropdownMenu = ({ row, navigate }) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const onDuplicateDispatchTicket = () => {
    navigate('/app/load-board/ticket-history/new', { state: { duplicateData: row } })
    handleClose()
  }

  const handleOpen = event => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <IconButton onClick={handleOpen}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={onDuplicateDispatchTicket}>Duplicate Dispatch Ticket</MenuItem>
        {/* Add more menu items here if needed */}
      </Menu>
    </>
  )
}

const getColumns = (defaultYardTimezone, inOrgMode, renderHeaderWithSort, navigate, theme) => {
  const tableColumns = [
    {
      field: 'externalId',
      headerName: 'Ticket #',
      align: 'left',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        const { externalId, recurringDispatchTicketId } = row || {}
        return (
          <>
            <Typography pl={1} variant="body2">
              {externalId}
            </Typography>
            {recurringDispatchTicketId && (
              <Tooltip
                title="Recurring Dispatch Ticket"
                placement="top"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  },
                }}
              >
                <Autorenew sx={{ fontSize: 16, color: theme.palette.text.hint }} />
              </Tooltip>
            )}
          </>
        )
      },
      renderHeader: () =>
        renderHeaderWithSort('Ticket #', SORTABLE_DISPATCH_TICKET_HISTORY_FIELDS.EXTERNAL_ID, {
          startAscending: false,
        }),
    },
    {
      field: 'statusName',
      headerName: 'Status',
      flex: 1.5,
      renderCell: ({ row }) => {
        const { status, statusName } = row || {}
        return (
          <>
            {DISPATCH_STATUS_ICONS_DICT[status]?.previewModal}
            <Typography pl={1} variant="body2">
              {statusName}
            </Typography>
          </>
        )
      },
      sortable: false,
    },
    {
      field: 'dateScheduled',
      headerName: 'Date Scheduled',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (row.dateScheduled ? dateFormatter(row.dateScheduled, defaultYardTimezone) : '---'),
      renderHeader: () =>
        renderHeaderWithSort('Date Scheduled', SORTABLE_DISPATCH_TICKET_HISTORY_FIELDS.DATE_SCHEDULED, {
          startAscending: false,
        }),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      sortable: false,
    },
    {
      field: 'yard',
      headerName: 'Yard',
      flex: 1,
      sortable: false,
    },
    {
      field: 'driver',
      headerName: 'Driver',
      flex: 2,
      sortable: false,
      renderHeader: () =>
        renderHeaderWithSort('Driver', SORTABLE_DISPATCH_TICKET_HISTORY_FIELDS.DRIVER, {
          startAscending: true,
        }),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 2,
      sortable: false,
      renderHeader: () =>
        renderHeaderWithSort('Customer', SORTABLE_DISPATCH_TICKET_HISTORY_FIELDS.CUSTOMER, {
          startAscending: true,
        }),
    },
    {
      field: 'location',
      headerName: 'Customer Location',
      flex: 2,
      sortable: false,
      renderHeader: () =>
        renderHeaderWithSort('Customer Location', SORTABLE_DISPATCH_TICKET_HISTORY_FIELDS.LOCATION, {
          startAscending: true,
        }),
    },
    {
      field: 'dateCreated',
      headerName: 'Date Created',
      flex: 1,
      renderCell: ({ row }) => (row.dateCreated ? dateFormatter(row.dateCreated, defaultYardTimezone) : '---'),
      sortable: false,
      renderHeader: () =>
        renderHeaderWithSort('Date Created', SORTABLE_DISPATCH_TICKET_HISTORY_FIELDS.CREATED_AT, {
          startAscending: false,
        }),
    },
    {
      field: 'actionButton',
      headerName: '',
      align: 'left',
      sortable: false,
      width: 60,
      renderCell: params => <DropdownMenu row={params?.row} navigate={navigate} />,
    },
  ]

  // If in org mode, we filter out the yard column
  if (!inOrgMode) {
    return tableColumns.filter(column => column.field !== 'yard')
  }

  return tableColumns
}

const DispatchTicketHistoryGrid = props => {
  const classes = useStyles()
  const navigate = useNavigate()
  const theme = useTheme()
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const { inOrgMode } = useSelector(state => state.ui)
  const renderHeaderWithSort = useMemo(
    () => generateRenderHeaderFunction(props.filter, props.setFilter),
    [props.filter],
  )

  const tableColumns = getColumns(defaultYardTimezone, inOrgMode, renderHeaderWithSort, navigate, theme)

  const handleRowClick = row => {
    navigate(`/app/load-board/ticket-history/${row.id}`)
  }

  return (
    <DataGridWithSearchV2
      {...props}
      className={classes.root}
      columns={tableColumns}
      textSearchPlaceholder="Search for tickets"
      onRowClick={handleRowClick}
      autoPageSize={false}
      defaultFilter={props.defaultFilter}
    />
  )
}

DispatchTicketHistoryGrid.propTypes = DataGridWithSearchV2.propTypes

DispatchTicketHistoryGrid.defaultProps = DataGridWithSearchV2.defaultProps

export default DispatchTicketHistoryGrid
